import React, { useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import { InfoCard, Progress, ErrorPanel } from '@backstage/core-components';
import {
  Box,
  Tooltip,
  Typography,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { parseScorecardExemptions } from './exemptions';
import { Donut } from './Donut';
import { useEntityChecks } from './hooks';
import { useCampaignOwnedCheckIds } from './useCampaignOwnedChecks';

type Props = {
  title: string;
  description: string;
  checksId: string[];
};

const MAX_VISIBLE = 5;

const useStyles = makeStyles<Theme>(theme => ({
  body: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: '160px auto',
    columnGap: theme.spacing(3),
    rowGap: 0,
    minHeight: 200,
  },
  bodyExpanded: {
    gridTemplateRows: 'auto auto',
  },
  donutCol: {
    gridColumn: 1,
    gridRow: 1,
    alignSelf: 'center',
    justifySelf: 'center',
  },
  rowsCol: {
    gridColumn: 2,
    gridRow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  moreCol: {
    gridColumn: 2,
    gridRow: 2,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    fontSize: '0.875rem',
  },
  rowName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: theme.spacing(1),
  },
  pass: {
    color: theme.palette.success.main,
  },
  fail: {
    color: theme.palette.error.main,
  },
  more: {
    paddingTop: theme.spacing(0.5),
    color: theme.palette.primary.main,
    fontSize: '0.8rem',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-block',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const BidoneScorecardCard = ({
  title,
  description,
  checksId,
}: Props) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { entity } = useEntity();
  const exempt = parseScorecardExemptions(entity);
  const owned = useCampaignOwnedCheckIds();
  const enabled = checksId.filter(id => !exempt.has(id) && !owned.has(id));

  const { data, isLoading: loading, error } = useEntityChecks(
    getCompoundEntityRef(entity),
    enabled,
  );

  if (enabled.length === 0) {
    return null;
  }

  const results = data ?? [];
  const passed = results.filter(r => r.result === true).length;
  const total = results.length;
  const visible = expanded ? results : results.slice(0, MAX_VISIBLE);
  const hidden = Math.max(0, results.length - MAX_VISIBLE);

  return (
    <InfoCard title={title} subheader={description}>
      {loading && <Progress />}
      {error && <ErrorPanel error={error} />}
      {!loading && !error && (
        <Box
          className={`${classes.body}${
            expanded ? ` ${classes.bodyExpanded}` : ''
          }`}
        >
          <Box className={classes.donutCol}>
            <Donut passed={passed} total={total} size={112} />
          </Box>
          <Box className={classes.rowsCol}>
            {visible.map(r => (
              <Tooltip
                key={r.check.id}
                title={r.check.description || r.check.name}
                arrow
              >
                <Box className={classes.row}>
                  <span className={classes.rowName}>{r.check.name}</span>
                  {r.result === true ? (
                    <CheckIcon
                      fontSize="small"
                      className={classes.pass}
                    />
                  ) : (
                    <CloseIcon
                      fontSize="small"
                      className={classes.fail}
                    />
                  )}
                </Box>
              </Tooltip>
            ))}
          </Box>
          {hidden > 0 && (
            <Box className={classes.moreCol}>
              <Typography
                className={classes.more}
                onClick={() => setExpanded(e => !e)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded(v => !v);
                  }
                }}
              >
                {expanded ? 'Show fewer' : `+${hidden} more…`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </InfoCard>
  );
};
